import {
  MOLECULE_LOT_FIELD_NAMES,
  MOLECULE_LOT_SCHEMA_INTERFACE_ID,
} from 'chemistry/molecule-lots/MoleculeLotConstants';
import {isSchemaTag, Tag} from 'folder-items/FileConstants';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {
  GetLotPropertiesQuery_Query as GetLotPropertiesQueryResponse,
  GetLotPropertiesQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetLotPropertiesQuery';
import {GET_LOT_PROPERTIES_QUERY} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {
  CustomResolutionQueryType,
  GetLotPropertiesQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import PreciseDecimal from 'util/precise-decimal/PreciseDecimal';

export interface LotProperties {
  parentMoleculeId: string | null;
  parentMoleculeName: string | null;
  purity: string | null;
  concentrationMolar: string | null;
  concentrationMolarUnitId: string | null;
  concentrationMolarUnitSymbol: string | null;
  concentrationDensity: string | null;
  concentrationDensityUnitId: string | null;
  concentrationDensityUnitSymbol: string | null;
}

/**
 * Canonicalizes a decimal string via PreciseDecimal (e.g. normalizes scientific notation to an
 * uppercase 'E') so that every consumer of LotProperties compares/formats purity and
 * concentration values consistently, regardless of how the value happens to be formatted on the
 * schema tag. Falls back to the raw value if it can't be parsed as a decimal.
 */
function canonicalizeDecimalString(value: string | null | undefined): string | null {
  if (value == null) {
    return null;
  }
  return PreciseDecimal.ofOrUndefined(value)?.value ?? value;
}

/**
 * Extracts the precision-preserving decimal string from a FLOAT schema tag, preferring
 * `schemaTag.value` (the text_value the backend already preserves for precision). Falls back to
 * stringifying `schemaTag.floatValue` if `value` is unset, so that legacy/malformed tags that
 * only populate the numeric column don't silently regress to null.
 */
function extractPreciseDecimalValue(schemaTag: {
  value?: string | null;
  floatValue?: number | null;
}): string | null {
  if (schemaTag.value != null) {
    return canonicalizeDecimalString(schemaTag.value);
  }
  if (schemaTag.floatValue != null) {
    return canonicalizeDecimalString(String(schemaTag.floatValue));
  }
  return null;
}

export function extractLotProperties(tagsJson: Array<Tag>): LotProperties {
  let parentMoleculeId: string | null = null;
  let parentMoleculeName: string | null = null;
  let purity: string | null = null;
  let concentrationMolar: string | null = null;
  let concentrationMolarUnitId: string | null = null;
  let concentrationMolarUnitSymbol: string | null = null;
  let concentrationDensity: string | null = null;
  let concentrationDensityUnitId: string | null = null;
  let concentrationDensityUnitSymbol: string | null = null;

  for (const tag of tagsJson) {
    if (!isSchemaTag(tag)) {
      continue;
    }
    const schemaTag = tag;
    const systemName = schemaTag.tagSchemaField.systemName;

    switch (systemName) {
      case MOLECULE_LOT_FIELD_NAMES.MOLECULE_LINK.systemName: {
        const targetFile = schemaTag.targetFiles?.[0];
        if (targetFile) {
          parentMoleculeId = targetFile.id;
          parentMoleculeName = targetFile.name ?? null;
        }
        break;
      }
      case MOLECULE_LOT_FIELD_NAMES.PURITY.systemName:
        purity = extractPreciseDecimalValue(schemaTag);
        break;
      case MOLECULE_LOT_FIELD_NAMES.CONCENTRATION_MOLAR.systemName:
        concentrationMolar = extractPreciseDecimalValue(schemaTag);
        concentrationMolarUnitId = schemaTag.tagSchemaField.unitApiIdentifier ?? null;
        concentrationMolarUnitSymbol = schemaTag.tagSchemaField.unitSymbol ?? null;
        break;
      case MOLECULE_LOT_FIELD_NAMES.CONCENTRATION_DENSITY.systemName:
        concentrationDensity = extractPreciseDecimalValue(schemaTag);
        concentrationDensityUnitId = schemaTag.tagSchemaField.unitApiIdentifier ?? null;
        concentrationDensityUnitSymbol = schemaTag.tagSchemaField.unitSymbol ?? null;
        break;
      default:
        break;
    }
  }

  return {
    parentMoleculeId,
    parentMoleculeName,
    purity,
    concentrationMolar,
    concentrationMolarUnitId,
    concentrationMolarUnitSymbol,
    concentrationDensity,
    concentrationDensityUnitId,
    concentrationDensityUnitSymbol,
  };
}

/**
 * Returns true if the given entity node implements the molecule lot schema interface.
 */
export function implementsMoleculeLotInterface(node: {
  tagSchema: {interfaces: Array<{id: string}> | undefined | null} | null;
}): boolean {
  return node.tagSchema?.interfaces?.some((iface) => iface.id === MOLECULE_LOT_SCHEMA_INTERFACE_ID) ?? false;
}

export async function bulkResolveGetLotPropertiesToServerResolutionResult(
  queries: Array<GetLotPropertiesQuery>
): Promise<Array<ServerResolutionResult>> {
  const lotIds = queries.map((query) => query.lotId);

  const queryResult = await doQueryWithoutCache<
    GetLotPropertiesQueryResponse,
    GetLotPropertiesQueryVariables
  >({query: GET_LOT_PROPERTIES_QUERY}, {variables: {lotIds}});

  const propertiesByLotId = new Map<string, LotProperties>();
  const nonLotEntityIds = new Set<string>();
  for (const bioentity of queryResult.bioentities) {
    if (isInaccessible(bioentity)) {
      continue;
    }
    if (!implementsMoleculeLotInterface(bioentity)) {
      nonLotEntityIds.add(bioentity.id);
      continue;
    }
    propertiesByLotId.set(bioentity.id, extractLotProperties(bioentity.tagsJson));
  }

  return queries.map((query) => {
    if (nonLotEntityIds.has(query.lotId)) {
      return makeCustomResolutionResultFromTypedValue(
        null,
        'Selected entity is not a molecule lot. Choose an entity that implements the molecule lot system category.'
      );
    }
    const properties = propertiesByLotId.get(query.lotId);
    if (properties == null) {
      return makeCustomResolutionResultFromTypedValue(null, 'Could not retrieve lot properties');
    }
    // Return the lot properties as a JSON typed value for downstream consumption
    return makeCustomResolutionResultFromTypedValue({
      type: BaseTypedValueType.JSON,
      jsonValue: properties,
    } as TypedValue);
  });
}

export function getLotPropertiesCustomEval(
  typedValue: TypedValue,
  _columnDefinition: unknown,
  _cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  // If there's no lot value, nothing to resolve
  if (typedValue == null || typedValue.type !== BaseTypedValueType.BIOENTITY) {
    return {val: typedValue, error: null, locked: false};
  }

  const lotId = typedValue.bioentitySummary.id;

  // Trigger the resolution query so it's cached for dependent columns
  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_LOT_PROPERTIES,
      lotId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );

  // If there was a resolution error (loading, network, not a lot, etc.), propagate it
  if (error != null) {
    return {val: typedValue, error, locked: false};
  }

  // Check for dual concentration (both molar and density defined)
  if (resolvedValue != null && resolvedValue.type === BaseTypedValueType.JSON) {
    const properties = resolvedValue.jsonValue as LotProperties;
    if (properties.concentrationMolar != null && properties.concentrationDensity != null) {
      return {
        val: typedValue,
        error: new ExcessUtil.ValueError(
          'Lot has both molar and density concentration defined. Only one is allowed.'
        ),
        locked: false,
      };
    }
  }

  // Return the lot value as-is (the lot cell just displays the lot entity)
  // The resolved properties are cached and consumed by dependent columns (molecule, purity, concentration)
  return {val: typedValue, error: null, locked: false};
}
